import { HttpInterceptorFn, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, EMPTY, filter, switchMap, take, throwError } from 'rxjs';

import { IdentityStash } from '../stash';
import { AuthService } from '../service';
import { AuthRefreshState } from './auth-refresh-state';
import { Identity } from '../model';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const identityStash = inject(IdentityStash);
    const authService = inject(AuthService);
    const platformId = inject(PLATFORM_ID);
    const router = inject(Router);
    const refreshState = inject(AuthRefreshState);

    // Skip the interceptor for refresh endpoint
    if (req.url.includes('/auth/refresh')) {
        return next(req);
    }

    const identity = identityStash.fetch();
    let previewToken: string | undefined;
    if (isPlatformServer(platformId)) {
        // In Angular SSR, the Router's raw url contains the full initial server query string
        const fullServerUrl = router.url;
        const urlParams = new URLSearchParams(fullServerUrl.split('?')[1] || '');
        previewToken = urlParams.get('previewToken') ?? undefined;
    } else if (isPlatformBrowser(platformId)) {
        // In the browser, read directly from the window location state
        const urlParams = new URLSearchParams(window.location.search);
        previewToken = urlParams.get('previewToken') ?? undefined;
    }

    const headers: any = getHeaders(identity, previewToken);

    const request = addHeaders(req, headers);

    return next(request).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status !== 401) {
                return throwError(() => error);
            }

            // Wait for ongoing refresh
            if (refreshState.refreshing()) {
                return refreshState.refresh$.pipe(
                    filter(Boolean),
                    take(1),
                    switchMap(identity =>
                        next(addHeaders(req, getHeaders(identity)))
                    )
                );
            }

            // Start refresh
            refreshState.refreshing.set(true);
            refreshState.refresh$.next(null);

            return authService.refresh().pipe(
                switchMap(identity => {
                    // ✅ Refresh token rotation
                    // The refresh API returns a new access + refresh token
                    if (identity) {
                        identityStash.save(identity);
                        refreshState.refresh$.next(identity);
                        refreshState.refreshing.set(false);
                    }

                    return next(addHeaders(req, getHeaders(identity)));
                }),
                catchError(() => {
                    refreshState.refreshing.set(false);
                    identityStash.clear();
                    router.navigate(['/login'], { queryParams: { redirect: router.url } });
                    return EMPTY;
                })
            );
        })
    );
};

function getHeaders(identity?: Identity, previewToken?: string): any {
    let headers: any = identity?.token ? { Authorization: `Bearer ${identity.token}` } : undefined;

    if (previewToken) {
        if (headers) {
            headers['X-Preview-Token'] = previewToken;
        } else {
            headers = { 'X-Preview-Token': previewToken };
        }
    }

    return headers;
}

function addHeaders(req: HttpRequest<any>, headers: any): HttpRequest<any> {
    return headers ? req.clone({ setHeaders: headers }) : req;
}
